<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

import StudioSection from '../components/StudioSection.vue';
import BotVsCsReport from '../components/BotVsCsReport.vue';
import { barWidthClass } from '../components/barWidth';
import {
  buildSetupChecklist,
  checklistProgress,
} from '../components/setupChecklist';
import { useAttachedInboxes } from '../components/useAttachedInboxes';

const props = defineProps({
  bot: {
    type: Object,
    required: true,
  },
});

const route = useRoute();
const store = useStore();
const { t } = useI18n();

const PERIODS = ['7d', '30d'];
const period = ref('7d');
const stats = useMapGetter('aiBotStats/getRecords');
const uiFlags = useMapGetter('aiBotStats/getUIFlags');
const documents = useMapGetter('aiBotDocuments/getRecords');
const answers = useMapGetter('aiBotAnswers/getRecords');

const botId = computed(() => route.params.botId);
const attachedInboxes = useAttachedInboxes(botId);

const fetchStats = () => {
  store.dispatch('aiBotStats/get', {
    botId: botId.value,
    period: period.value,
  });
};

watch(period, fetchStats, { immediate: true });

onMounted(() => {
  store.dispatch('aiBotDocuments/get', { botId: props.bot.id });
  store.dispatch('aiBotAnswers/get', {
    botId: props.bot.id,
    matchType: 'exact',
  });
});

const percent = value => `${Math.round((value || 0) * 100)}%`;
const formatNumber = value => Number(value || 0).toLocaleString();

const cards = computed(() => [
  {
    key: 'messages',
    icon: 'i-lucide-messages-square',
    tone: 'bg-n-brand/10 text-n-blue-11',
    label: t('AI_AGENT.OVERVIEW.CARDS.MESSAGES'),
    value: formatNumber(stats.value.messages),
  },
  {
    key: 'handoff_rate',
    icon: 'i-lucide-life-buoy',
    tone: 'bg-n-amber-9/10 text-n-amber-11',
    label: t('AI_AGENT.OVERVIEW.CARDS.HANDOFF_RATE'),
    value: percent(stats.value.handoff_rate),
  },
  {
    key: 'unanswered',
    icon: 'i-lucide-circle-help',
    tone: 'bg-n-ruby-9/10 text-n-ruby-11',
    label: t('AI_AGENT.OVERVIEW.CARDS.UNANSWERED'),
    value: formatNumber(stats.value.unanswered),
  },
  {
    key: 'p95_ms',
    icon: 'i-lucide-timer',
    tone: 'bg-n-teal-9/10 text-n-teal-11',
    label: t('AI_AGENT.OVERVIEW.CARDS.P95'),
    value: t('AI_AGENT.OVERVIEW.MS', { n: formatNumber(stats.value.p95_ms) }),
  },
]);

const tokens = computed(() => stats.value.tokens ?? 0);
const tokenCap = computed(() => stats.value.token_cap ?? null);
const tokenUsageClass = computed(() =>
  barWidthClass(tokens.value, tokenCap.value)
);
const isNearTokenCap = computed(
  () => tokenCap.value && tokens.value / tokenCap.value >= 0.8
);

const layers = computed(() => {
  const byLayer = stats.value.by_layer || {};
  const max = Math.max(...Object.values(byLayer), 0);
  const total = Object.values(byLayer).reduce((sum, n) => sum + n, 0);
  return Object.entries(byLayer)
    .sort((a, b) => b[1] - a[1])
    .map(([name, count]) => ({
      name,
      count,
      share: total ? `${Math.round((count / total) * 100)}%` : '0%',
      widthClass: barWidthClass(count, max),
    }));
});

const checklist = computed(() =>
  buildSetupChecklist({
    botConfig: props.bot.bot_config || {},
    inboxCount: attachedInboxes.value.length,
    documentCount: documents.value.length,
    answerCount: answers.value.length,
  })
);

const progress = computed(() => checklistProgress(checklist.value));
const progressClass = computed(() =>
  barWidthClass(progress.value.done, progress.value.total)
);

const stepTarget = step =>
  step.routeName
    ? { name: step.routeName, params: route.params }
    : {
        name: 'settings_inbox_list',
        params: { accountId: route.params.accountId },
      };
</script>

<template>
  <div class="flex flex-col gap-5">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <h3 class="text-heading-3 text-n-slate-12">
        {{ $t('AI_AGENT.OVERVIEW.PERFORMANCE') }}
      </h3>
      <div
        class="inline-flex gap-1 p-1 rounded-xl bg-n-alpha-1 outline outline-1 -outline-offset-1 outline-n-weak"
      >
        <button
          v-for="option in PERIODS"
          :key="option"
          type="button"
          class="px-3 py-1 transition-colors duration-150 rounded-lg text-button-small focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand motion-reduce:transition-none"
          :class="
            period === option
              ? 'bg-n-solid-3 text-n-slate-12 shadow-sm'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          :aria-pressed="period === option"
          @click="period = option"
        >
          {{ $t(`AI_AGENT.OVERVIEW.PERIOD.${option.toUpperCase()}`) }}
        </button>
      </div>
    </div>

    <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <div
        v-for="card in cards"
        :key="card.key"
        class="flex flex-col gap-3 p-4 rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container"
      >
        <div class="flex items-center justify-between gap-2">
          <span class="truncate text-label-small text-n-slate-11">
            {{ card.label }}
          </span>
          <span
            class="flex items-center justify-center rounded-lg size-7 shrink-0"
            :class="card.tone"
          >
            <span :class="card.icon" class="size-3.5" />
          </span>
        </div>
        <span
          class="text-2xl font-semibold tracking-tight tabular-nums text-n-slate-12"
          :data-testid="`stat-${card.key}`"
        >
          {{ uiFlags.isFetching ? '—' : card.value }}
        </span>
      </div>
    </div>

    <BotVsCsReport
      :report="stats.bot_vs_cs"
      :is-fetching="uiFlags.isFetching"
    />

    <div
      class="flex flex-col gap-3 p-4 rounded-xl bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-container"
    >
      <div class="flex flex-wrap items-center justify-between gap-2">
        <span class="flex items-center gap-2 text-label-small text-n-slate-11">
          <span class="i-lucide-coins size-3.5" />
          {{ $t('AI_AGENT.OVERVIEW.CARDS.TOKENS') }}
        </span>
        <span
          class="text-heading-3 tabular-nums text-n-slate-12"
          data-testid="stat-tokens"
        >
          {{
            uiFlags.isFetching
              ? '—'
              : `${formatNumber(tokens)} / ${tokenCap ? formatNumber(tokenCap) : '∞'}`
          }}
        </span>
      </div>
      <div class="w-full h-2 overflow-hidden rounded-full bg-n-alpha-2">
        <div
          class="h-2 rounded-full"
          :class="[
            tokenCap ? tokenUsageClass : 'w-0',
            isNearTokenCap ? 'bg-n-amber-9' : 'bg-n-brand',
          ]"
        />
      </div>
      <p v-if="!tokenCap" class="text-label-small text-n-slate-10">
        {{ $t('AI_AGENT.OVERVIEW.NO_TOKEN_CAP') }}
      </p>
    </div>

    <div class="grid grid-cols-1 gap-5 lg:grid-cols-5">
      <StudioSection
        class="lg:col-span-3"
        :title="$t('AI_AGENT.OVERVIEW.BY_LAYER')"
        :description="$t('AI_AGENT.OVERVIEW.BY_LAYER_HELP')"
        icon="i-lucide-layers"
      >
        <p
          v-if="!layers.length && !uiFlags.isFetching"
          class="py-6 text-center text-body-main text-n-slate-10"
        >
          {{ $t('AI_AGENT.OVERVIEW.NO_DATA') }}
        </p>
        <ul v-else class="flex flex-col gap-3">
          <li
            v-for="layer in layers"
            :key="layer.name"
            class="grid items-center grid-cols-[7rem_1fr_auto] gap-3"
          >
            <span class="font-mono truncate text-label-small text-n-slate-12">
              {{ layer.name }}
            </span>
            <div class="h-2 overflow-hidden rounded-full bg-n-alpha-2">
              <div
                class="h-2 rounded-full bg-n-brand"
                :class="layer.widthClass"
              />
            </div>
            <span class="text-right tabular-nums text-label-small">
              <span class="text-n-slate-12">{{ layer.count }}</span>
              <span class="ml-1 text-n-slate-10">{{ layer.share }}</span>
            </span>
          </li>
        </ul>
      </StudioSection>

      <StudioSection
        class="lg:col-span-2"
        :title="$t('AI_AGENT.OVERVIEW.CHECKLIST.TITLE')"
        :description="
          $t('AI_AGENT.OVERVIEW.CHECKLIST.PROGRESS', {
            done: progress.done,
            total: progress.total,
          })
        "
        icon="i-lucide-rocket"
      >
        <div class="w-full h-1.5 overflow-hidden rounded-full bg-n-alpha-2">
          <div class="h-1.5 rounded-full bg-n-teal-9" :class="progressClass" />
        </div>
        <ul class="flex flex-col gap-1 -mx-2">
          <li v-for="step in checklist" :key="step.key">
            <router-link
              :to="stepTarget(step)"
              class="flex items-start gap-3 p-2 transition-colors rounded-lg group hover:bg-n-alpha-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand motion-reduce:transition-none"
              :data-testid="`checklist-${step.key}`"
            >
              <span
                class="flex items-center justify-center mt-0.5 rounded-full size-5 shrink-0"
                :class="
                  step.done
                    ? 'bg-n-teal-9 text-white'
                    : 'outline outline-1 -outline-offset-1 outline-n-strong'
                "
              >
                <span v-if="step.done" class="i-lucide-check size-3" />
              </span>
              <span class="flex flex-col flex-1 min-w-0">
                <span
                  class="text-body-main"
                  :class="
                    step.done
                      ? 'text-n-slate-11 line-through'
                      : 'text-n-slate-12'
                  "
                >
                  {{
                    $t(`AI_AGENT.OVERVIEW.CHECKLIST.STEPS.${step.key}.TITLE`)
                  }}
                </span>
                <span
                  v-if="!step.done"
                  class="text-label-small text-n-slate-10"
                >
                  {{ $t(`AI_AGENT.OVERVIEW.CHECKLIST.STEPS.${step.key}.HINT`) }}
                </span>
              </span>
              <span
                v-if="!step.done"
                class="i-lucide-chevron-right size-4 mt-0.5 text-n-slate-10 transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none"
              />
            </router-link>
          </li>
        </ul>
      </StudioSection>
    </div>
  </div>
</template>
